import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Search, FileText, ArrowRight, Check } from "lucide-react";
import { API, axios, formatIcelandicNumber } from "@/utils/constants";

export const SkuldbreytingDialog = ({
  open, onOpenChange,
  skuldbreytingLoanNumber, setSkuldbreytingLoanNumber,
  skuldbreytingLoan, setSkuldbreytingLoan,
  skuldbreytingSelectedLoans, setSkuldbreytingSelectedLoans,
  skuldbreytingStep, setSkuldbreytingStep,
  skuldbreytingNewLoanType, setSkuldbreytingNewLoanType,
  setSkuldbreytingOldLoanId, setSkuldbreytingOldLoanIds,
  setShowSkuldabrefForm, setShowVedskuldabrefForm, setShowKaupleiguForm,
  setSkuldabrefFormData, setVedskuldabrefFormData, setKaupleiguFormData,
  applications, settings,
  loanNumberMap, fmtLoanNum,
}) => {
  const showSkuldbreytingDialog = open;
  const setShowSkuldbreytingDialog = onOpenChange;
  return (
<Dialog open={showSkuldbreytingDialog} onOpenChange={(open) => {
  setShowSkuldbreytingDialog(open);
  if (!open) {
    // Reset state when closing
    setSkuldbreytingLoanNumber("");
    setSkuldbreytingLoan(null);
    setSkuldbreytingSelectedLoans([]);
    setSkuldbreytingStep(1);
    setSkuldbreytingNewLoanType(null);
  }
}}>
  <DialogContent className="max-w-md">
    <DialogHeader>
      <DialogTitle className="text-sm">Skuldbreyting</DialogTitle>
    </DialogHeader>
    
    {/* Step 1: Loan Lookup */}
    {skuldbreytingStep === 1 && (
      <div className="space-y-3 py-2">
        <div>
          <Label className="text-[10px]">Láns nr. eða kennitala</Label>
          <div className="flex gap-2">
            <Input
              type="text"
              className="h-7 text-xs flex-1"
              value={skuldbreytingLoanNumber}
              onChange={async (e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setSkuldbreytingLoanNumber(value);
                
                if (value) {
                  try {
                    const response = await axios.get(`${API}/loans`);
                    const allLoans = response.data;
                    
                    if (value.length === 10) {
                      // Kennitala search — may return multiple loans for same borrower
                      const found = allLoans.filter(loan =>
                        loan.borrower_info?.ssn === value &&
                        (loan.status === 'approved' || loan.status === 'disbursed')
                      );
                      if (found.length === 1) {
                        setSkuldbreytingLoan(found[0]);
                      } else if (found.length > 1) {
                        // Auto-add all found loans for this kennitala
                        const notYetAdded = found.filter(f => !skuldbreytingSelectedLoans.find(s => s.id === f.id));
                        if (notYetAdded.length > 0) {
                          setSkuldbreytingSelectedLoans(prev => [...prev, ...notYetAdded]);
                          setSkuldbreytingLoanNumber("");
                          setSkuldbreytingLoan(null);
                          toast.success(`${notYetAdded.length} lán fundust fyrir kennitölu ${value}`);
                        } else {
                          setSkuldbreytingLoan(null);
                        }
                      } else {
                        setSkuldbreytingLoan(null);
                      }
                    } else {
                      // Loan number search
                      const found = allLoans.find(loan => {
                        const loanNum = loanNumberMap[loan.id];
                        return loanNum && loanNum.toString() === value;
                      });
                      setSkuldbreytingLoan(found || null);
                    }
                  } catch (error) {
                    console.error("Error fetching loan:", error);
                  }
                } else {
                  setSkuldbreytingLoan(null);
                }
              }}
              placeholder="Nr. eða kennitala"
              maxLength={10}
            />
            {skuldbreytingLoan && !skuldbreytingSelectedLoans.find(l => l.id === skuldbreytingLoan.id) && (
              <Button
                size="sm"
                className="h-7 text-xs bg-green-600 hover:bg-green-700"
                onClick={() => {
                  setSkuldbreytingSelectedLoans([...skuldbreytingSelectedLoans, skuldbreytingLoan]);
                  setSkuldbreytingLoanNumber("");
                  setSkuldbreytingLoan(null);
                }}
              >
                + Bæta við
              </Button>
            )}
          </div>
        </div>
        
        {/* Selected loans list */}
        {skuldbreytingSelectedLoans.length > 0 && (
          <div className="space-y-2">
            <Label className="text-[10px] font-semibold">Valin lán ({skuldbreytingSelectedLoans.length})</Label>
            {skuldbreytingSelectedLoans.map((loan, index) => (
              <div key={loan.id} className="bg-blue-50 border border-blue-200 rounded p-2 relative">
                <button
                  className="absolute top-1 right-1 text-red-600 hover:text-red-800 font-bold text-xs"
                  onClick={() => {
                    setSkuldbreytingSelectedLoans(skuldbreytingSelectedLoans.filter(l => l.id !== loan.id));
                  }}
                >
                  ✕
                </button>
                <div className="grid grid-cols-2 gap-x-2 pr-6" style={{fontSize: '10px'}}>
                  <div>
                    <p className="text-gray-600">{loanNumberMap[loan.id] ? fmtLoanNum(loanNumberMap[loan.id]) : 'Láns nr. ????'}</p>
                    <p className="font-semibold">{loan.borrower_info.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Eftirstöðvar</p>
                    <p className="font-semibold text-blue-700">
                      {(() => {
                        // Calculate remaining balance (same logic as before)
                        if (loan.is_legacy && loan.payment_schedule) {
                          const paidMonthNumbers = Object.entries(loan.scheduled_payments_status || {})
                            .filter(([month, status]) => status === 'paid')
                            .map(([month]) => parseInt(month))
                            .sort((a, b) => b - a);
                          
                          if (paidMonthNumbers.length > 0) {
                            const lastPaidMonth = paidMonthNumbers[0];
                            const scheduleEntry = loan.payment_schedule.find(entry => entry.month === lastPaidMonth);
                            if (scheduleEntry && scheduleEntry.balance !== undefined) {
                              let balance = scheduleEntry.balance;
                              const today = new Date();
                              const dayOfMonth = today.getDate();
                              const annualRate = loan.interest_rate || 0;
                              const monthlyInterest = balance * (annualRate / 100) / 12;
                              const accruedInterest = (monthlyInterest / 30) * dayOfMonth;
                              return formatIcelandicNumber(Math.round(balance + accruedInterest));
                            }
                          }
                          return formatIcelandicNumber(Math.round(loan.loan_amount));
                        } else {
                          const loanAmount = loan.loan_amount;
                          const lantokugjald = loan.lantokugjald ?? 0;
                          const totalAmount = loanAmount + lantokugjald;
                          const totalPaid = (loan.payments || []).reduce((sum, p) => sum + p.amount, 0);
                          let remaining = Math.max(0, totalAmount - totalPaid);
                          
                          const today = new Date();
                          const dayOfMonth = today.getDate();
                          const annualRate = settings.vextir;
                          const monthlyInterest = remaining * (annualRate / 100) / 12;
                          const accruedInterest = (monthlyInterest / 30) * dayOfMonth;
                          remaining += accruedInterest;
                          
                          return formatIcelandicNumber(Math.round(remaining));
                        }
                      })()} kr.
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Total remaining balance */}
            <div className="bg-green-50 border border-green-200 rounded p-2">
              <div className="flex justify-between items-center" style={{fontSize: '11px'}}>
                <span className="font-semibold">Heildar eftirstöðvar:</span>
                <span className="font-bold text-green-700">
                  {(() => {
                    let total = 0;
                    skuldbreytingSelectedLoans.forEach(loan => {
                      if (loan.is_legacy && loan.payment_schedule) {
                        const paidMonthNumbers = Object.entries(loan.scheduled_payments_status || {})
                          .filter(([month, status]) => status === 'paid')
                          .map(([month]) => parseInt(month))
                          .sort((a, b) => b - a);
                        
                        if (paidMonthNumbers.length > 0) {
                          const lastPaidMonth = paidMonthNumbers[0];
                          const scheduleEntry = loan.payment_schedule.find(entry => entry.month === lastPaidMonth);
                          if (scheduleEntry && scheduleEntry.balance !== undefined) {
                            let balance = scheduleEntry.balance;
                            const today = new Date();
                            const dayOfMonth = today.getDate();
                            const annualRate = loan.interest_rate || 0;
                            const monthlyInterest = balance * (annualRate / 100) / 12;
                            const accruedInterest = (monthlyInterest / 30) * dayOfMonth;
                            total += balance + accruedInterest;
                          }
                        } else {
                          total += loan.loan_amount;
                        }
                      } else {
                        const loanAmount = loan.loan_amount;
                        const lantokugjald = loan.lantokugjald ?? 0;
                        const totalAmount = loanAmount + lantokugjald;
                        const totalPaid = (loan.payments || []).reduce((sum, p) => sum + p.amount, 0);
                        let remaining = Math.max(0, totalAmount - totalPaid);
                        
                        const today = new Date();
                        const dayOfMonth = today.getDate();
                        const annualRate = settings.vextir;
                        const monthlyInterest = remaining * (annualRate / 100) / 12;
                        const accruedInterest = (monthlyInterest / 30) * dayOfMonth;
                        remaining += accruedInterest;
                        
                        total += remaining;
                      }
                    });
                    return formatIcelandicNumber(Math.round(total));
                  })()} kr.
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Real-time loan details for currently typed loan */}
        {skuldbreytingLoan && !skuldbreytingSelectedLoans.find(l => l.id === skuldbreytingLoan.id) && (
          <div className="bg-blue-50 border border-blue-200 rounded p-2 space-y-1">
            <div className="grid grid-cols-2 gap-x-2" style={{fontSize: '10px'}}>
              <div>
                <p className="text-gray-600">Lántaki</p>
                <p className="font-semibold">{skuldbreytingLoan.borrower_info.full_name}</p>
              </div>
              <div>
                <p className="text-gray-600">Kennitala</p>
                <p className="font-semibold">{skuldbreytingLoan.borrower_info.kennitala}</p>
              </div>
              <div>
                <p className="text-gray-600">Eftirstöðvar</p>
                <p className="font-semibold text-blue-700">
                  {formatIcelandicNumber(Math.round(skuldbreytingLoan.remaining_balance || skuldbreytingLoan.loan_amount))} kr.
                </p>
              </div>
              <div>
                <p className="text-gray-600">Mánuðir eftir</p>
                <p className="font-semibold">
                  {(() => {
                    const createdDate = new Date(skuldbreytingLoan.created_at);
                    const now = new Date();
                    const monthsPassed = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24 * 30));
                    const monthsLeft = Math.max(0, skuldbreytingLoan.requested_term_months - monthsPassed);
                    return monthsLeft;
                  })()} mánuðir
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex justify-end space-x-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[10px] px-2"
            onClick={() => setShowSkuldbreytingDialog(false)}
          >
            Hætta við
          </Button>
          <Button
            size="sm"
            className="h-6 text-[10px] px-2"
            disabled={!skuldbreytingLoan && skuldbreytingSelectedLoans.length === 0}
            onClick={() => {
              // If user typed a loan but didn't add it to the list, use single loan mode
              if (skuldbreytingLoan && skuldbreytingSelectedLoans.length === 0) {
                // Single loan mode (legacy behavior)
                setSkuldbreytingOldLoanId(skuldbreytingLoan.id);
                setSkuldbreytingOldLoanIds([skuldbreytingLoan.id]);
              } else {
                // Multiple loans mode (new behavior)
                setSkuldbreytingOldLoanIds(skuldbreytingSelectedLoans.map(l => l.id));
              }
              setSkuldbreytingStep(2);
            }}
          >
            {skuldbreytingSelectedLoans.length > 0 
              ? `Áfram (${skuldbreytingSelectedLoans.length} lán)` 
              : skuldbreytingLoan 
                ? 'Áfram (1 lán)' 
                : 'Áfram'}
          </Button>
        </div>
      </div>
    )}
    
    {/* Step 2: Choose New Loan Type */}
    {skuldbreytingStep === 2 && (
      <div className="space-y-3 py-2">
        <p className="text-xs text-gray-600">Veldu tegund nýs láns:</p>
        <div className="grid grid-cols-1 gap-2">
          <Button
            variant="outline"
            className="h-10 justify-start"
            onClick={() => {
              setSkuldbreytingNewLoanType('skuldabref');
              setSkuldbreytingStep(3);
            }}
          >
            <FileText className="h-4 w-4 mr-2" />
            Skuldabréf
          </Button>
          <Button
            variant="outline"
            className="h-10 justify-start"
            onClick={() => {
              setSkuldbreytingNewLoanType('vedskuldabref');
              setSkuldbreytingStep(3);
            }}
          >
            <FileText className="h-4 w-4 mr-2" />
            Veðskuldabréf
          </Button>
          <Button
            variant="outline"
            className="h-10 justify-start"
            onClick={() => {
              setSkuldbreytingNewLoanType('kaupleigusamning');
              setSkuldbreytingStep(3);
            }}
          >
            <FileText className="h-4 w-4 mr-2" />
            Kaupleigusamningur
          </Button>
        </div>
        <div className="flex justify-end space-x-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[10px] px-2"
            onClick={() => setSkuldbreytingStep(1)}
          >
            Til baka
          </Button>
        </div>
      </div>
    )}
    
    {/* Step 3: Form will be handled by opening the appropriate form with pre-filled data */}
    {skuldbreytingStep === 3 && (
      <div className="space-y-2 py-2">
        <p className="text-xs text-gray-600">Form opnast sjálfkrafa...</p>
      </div>
    )}
  </DialogContent>
</Dialog>

  );
};
